/* eslint-disable max-classes-per-file */
/* eslint-disable no-restricted-globals */
import type {
  APIReqOptions,
  APIResBody,
  APIResBodyEntry,
} from 'server/services/recommendation';
import { getRecommendations } from './api/recs';

export interface RexWorkerParams {
  uv: UniversalVar;
  qs: typeof window.__ALL_RECS_QUERIES__;
  options: typeof window.__GLOBAL_RECS_STATE__ & {
    screenWidth: number;
    visitorId: string;
  };
  customerID: string | undefined;
  taxonomy: string;
}

export interface RexWorkerData {
  options: APIReqOptions;
  recs: Record<string, APIResBodyEntry[]> | null;
}

export declare class RexWorker extends globalThis.Worker {
  constructor();
  postMessage(data: RexWorkerParams): void;
  onmessage: (ev: MessageEvent<RexWorkerData>) => void;
}

export interface RexWorkerImpl {
  postMessage(data: RexWorkerData): void;
  onmessage: (ev: MessageEvent<RexWorkerParams>) => void;
}

declare const self: RexWorkerImpl & WindowOrWorkerGlobalScope;
declare const Worker: typeof RexWorker;

export const limitString = (str: string | undefined, limit: number) =>
  str?.substring(0, limit);

const getEnv = (screenWidth: number) => {
  // eslint-disable-next-line no-underscore-dangle
  const tabletMinWidth = window.__TABLET_MIN_WIDTH__;
  return screenWidth < tabletMinWidth ? 'm' : 'd';
};

const getProdGroupId = (url: string): string | undefined => {
  const regex = /\/(\d+)\.prd$/;
  const match = url.match(regex);
  return match ? `${match?.[1]}` : undefined;
};

export const getItems = ({
  listing,
  product,
  basket,
}: UniversalVar): { itemNumbers: string[]; productGroupIds: string[] } => {
  const items: { itemNumbers: string[]; productGroupIds: string[] } = {
    itemNumbers: [],
    productGroupIds: [],
  };

  const addProductData = (
    id: string | undefined,
    productGroupId: string | undefined,
  ) => {
    if (id) {
      items.itemNumbers.push(id);
    }
    if (productGroupId) {
      items.productGroupIds.push(`${productGroupId}`);
    }
  };

  if (listing) {
    if (listing.lineItems?.length) {
      listing.lineItems.forEach((item) => {
        const { id, productGroupId } = item.product || {};
        addProductData(id, productGroupId);
      });
    }
  } else if (product) {
    const { id, url } = product;
    let { productGroupId } = product;
    addProductData(id, productGroupId);

    if (!productGroupId && url) {
      productGroupId = getProdGroupId(url);
      if (productGroupId) {
        items.productGroupIds.push(`${productGroupId}`);
      }
    }
  } else if (basket?.line_items?.length) {
    basket.line_items.forEach((item) => {
      const { id, productGroupId } = item.product || {};
      addProductData(id, productGroupId);
    });
  }

  return items;
};

export const getPrices = ({ product }: UniversalVar): number[] => {
  const prices: number[] = [];
  if (!product) {
    return [];
  }
  const addProductData = (price: number | undefined) => {
    if (price) {
      prices.push(price);
    }
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { unit_price, unit_sale_price } = product;
  const price = unit_sale_price
    ? parseFloat(unit_sale_price)
    : parseFloat(unit_price);
  addProductData(price);
  return prices;
};

const reduceRecs = (recs: APIResBody) =>
  recs.reduce<Record<string, APIResBodyEntry[]>>((prev, curr) => {
    const key = `${curr.provider}_${curr.placement}`;
    return { ...prev, [key]: [...(prev[key] ?? []), curr] };
  }, {});

export const createReqOptions = (
  uv: UniversalVar,
  options: any,
  customerID: string | undefined,
  taxonomy: string,
) => {
  const searchTerm = uv?.listing?.searchTerm || uv?.page?.searchTerm;
  const category = taxonomy || uv.listing?.category;

  return {
    items: getItems(uv),
    prices: getPrices(uv),
    environment: getEnv(options.screenWidth),
    visitorID: options.visitorId,
    keywords: limitString(searchTerm, 80),
    category,
    listSize: uv.listing?.numProducts || undefined,
    pageNumber: uv.listing?.numPagination || undefined,
    imageSize: '120x160',
    customerID,
  };
};

export const messageHandler: RexWorkerImpl['onmessage'] = async ({
  data: { uv, qs, options, customerID, taxonomy },
}) => {
  self.API_BASE_URL = `${location.origin}${options.API_BASE_URL}`;
  const queries = Object.values(qs).filter((q): q is NonNullable<typeof q> =>
    Boolean(q),
  );
  if (!queries.length) {
    console.error(new Error('recs_configs_unavailable'));
    return self.postMessage({ options: {}, recs: null });
  }
  const ReqOptions = createReqOptions(uv, options, customerID, taxonomy);
  const configs = queries.map(({ config }) => config);
  const placementsWithItemID = ['pdp_1', 'pdp_2', 'atb_1'];

  const filteredConfigs = configs.filter((config) => {
    if (
      config.provider === 'recs2' &&
      placementsWithItemID.includes(config.placement)
    ) {
      if (
        ReqOptions &&
        ReqOptions.items &&
        ReqOptions.items.productGroupIds &&
        ReqOptions.items.productGroupIds?.length < 1
      ) {
        return false;
      }
    }
    return true;
  });

  if (filteredConfigs.length === 0) {
    console.error(new Error('No valid configurations available'));
    return self.postMessage({ options: ReqOptions, recs: null });
  }

  const allRecs = await getRecommendations(filteredConfigs, ReqOptions);
  if (!allRecs) {
    console.error(new Error('recs_request_failed'));
    return self.postMessage({ options: ReqOptions, recs: null });
  }

  self.postMessage({ options: ReqOptions, recs: reduceRecs(allRecs) });
};

self.onmessage = messageHandler;

export default Worker;
export const WORKER_TEST = {
  getEnv,
  getProdGroupId,
  getPrices,
  getItems,
  reduceRecs,
};
